import { SlidersHorizontal } from 'lucide-react';

import type { GuideModule } from '../types';

/**
 * Every quoted label was re-checked against
 * `routes/settings/system-parameters/system-parameters-page.tsx`,
 * `components/SettingsForm.tsx` and `setting-catalog.ts`. The two keys
 * documented here are the real v1 catalogue
 * (`order.default_cutoff_time`, `order.cutoff_days_before`), not the
 * illustrative names from `design.md` §6.4/Anexo B.
 */
export const SYSTEM_PARAMETERS_MODULE: GuideModule = {
  id: 'system-parameters',
  title: 'Parámetros del sistema',
  icon: SlidersHorizontal,
  requiredPermission: 'settings.read',
  priority: 13,
  summary:
    'Acá se configuran valores operativos que rigen todo el back-office, empezando por el cierre de pedidos. Verlos requiere el permiso "Ver configuración del sistema"; editarlos requiere además "Modificar configuración del sistema". Cada cambio queda en un historial con fecha y quién lo hizo.',
  steps: [
    {
      title: 'Hora de cierre de pedidos',
      description:
        'El campo "Hora de cierre de pedidos", dentro de la categoría "Pedidos", define la hora del día (formato 24 horas) a partir de la cual el sistema deja de aceptar pedidos nuevos para la fecha de servicio correspondiente.',
      keyTerms: ['corte'],
    },
    {
      title: 'Días de anticipación del cierre',
      description:
        'El campo "Días de anticipación del cierre" define cuántos días antes de la fecha de servicio se aplica esa hora de cierre. Por ejemplo, con 1 día de anticipación, los pedidos del jueves cierran el miércoles a la hora configurada arriba.',
      keyTerms: ['corte'],
    },
    {
      title: 'Cierre automático de pedidos',
      description:
        'En la categoría "Pedidos", el interruptor "Cierre automático de pedidos" (encendido por defecto) hace que cada pedido se cierre solo a su hora de corte: se confirman los enviados y se cancelan los borradores. Solo actúa sobre pedidos de hoy en adelante: los días anteriores no se cierran automáticamente. Para apagarlo, desactivá el interruptor; se guarda en el momento, sin usar "Guardar". El botón "Cerrar corte" del Tablero del día sigue disponible siempre.',
      keyTerms: ['corte', 'cierre-de-corte'],
    },
    {
      title: 'Formato del número de recibo',
      description:
        'En la categoría "Pagos", el campo "Formato del número de recibo" define cómo se arma el número de cada recibo nuevo. Se escribe con tokens: {SEQUENCE} es obligatorio y {YYYYMM}, {YYYY} y {DD} son opcionales, una vez cada uno; el formato admite hasta 40 caracteres y, fuera de los tokens, solo letras, números y . - _ . Mientras escribís, el sistema muestra una vista previa del número resultante. Atención: los recibos ya emitidos conservan su número y la numeración continúa, así que cambiar el formato no la reinicia.',
      keyTerms: ['recibo'],
    },
    {
      title: 'Quién puede editar',
      description:
        'Con solo el permiso "Ver configuración del sistema" los campos se ven pero quedan deshabilitados, sin el botón "Guardar". Con "Modificar configuración del sistema" además podés modificar los valores y guardarlos: al hacer clic en "Guardar" se envía un cambio independiente por cada parámetro que hayas modificado.',
      keyTerms: [],
    },
    {
      title: 'Ver el historial de un parámetro',
      description:
        'Debajo de cada campo, si tenés permiso de auditoría, hay un botón "Ver historial". Al hacer clic muestra el último cambio con el formato "Último cambio: [fecha y hora] por [usuario]", o "Sin cambios registrados." si todavía no se modificó nunca.',
      keyTerms: [],
    },
  ],
  states: [],
  faq: [
    {
      question: '¿Por qué veo los campos pero no puedo editarlos?',
      answer:
        'Porque tenés el permiso "Ver configuración del sistema" pero no "Modificar configuración del sistema". Pedile a un administrador que te otorgue ese permiso si necesitás modificar estos valores.',
    },
    {
      question: '¿Por qué se confirmó mi pedido solo?',
      answer:
        'Porque el "Cierre automático de pedidos" está encendido: cada pedido se cierra solo a su hora de corte. Desactivalo en Pedidos si querés cerrar siempre a mano con "Cerrar corte".',
    },
    {
      question: '¿Guardar varios parámetros a la vez hace un solo cambio?',
      answer:
        'No. El sistema envía un cambio independiente por cada parámetro modificado, aunque hayas tocado varios antes de hacer clic en "Guardar".',
    },
    {
      question: '¿Qué pasa con los recibos ya emitidos si cambio el formato del número de recibo?',
      answer:
        'Conservan el número que ya tenían. El nuevo formato se aplica solo a los recibos que registres desde ese momento y la secuencia continúa, no vuelve a empezar.',
    },
    {
      question: '¿Quién puede ver el historial de cambios?',
      answer: 'Solo quien tiene permiso de auditoría ve el botón "Ver historial" debajo de cada campo.',
    },
  ],
  tips: [
    'Si cambiás la hora de cierre de pedidos, avisá al equipo comercial: afecta a todas las empresas, no a una en particular.',
  ],
};
